import {
  Box,
  Button,
  Card,
  FormHelperText,
  Grid,
  IconButton,
  LinearProgress,
  styled,
  useTheme
} from '@mui/material';
import { useEffect, useState } from 'react';
import PageHeader from './PageHeader';

import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Review } from 'src/services/ezzyEvents/apiService/responseModel';
import {
  QKEY,
  getRefData,
  getReviewById,
  saveReview
} from 'src/services/query-client';
import { ReviewDto } from 'src/services/query-client/dto';
import { RootState } from 'src/store';
import readQueryString from 'src/utility/readQueryString';
import * as Yup from 'yup';
import { parseApiError } from '../../../utility/parseToApiErrorMessage';
import ReviewContactCard from './review-contact-card';
import ReviewInfoCard from './review-info-card';

const MainContentWrapper = styled(Box)(
  () => `
  flex-grow: 1;
`
);

const IconButtonToggle = styled(IconButton)(
  ({ theme }) => `
  width: ${theme.spacing(6)};
  height: ${theme.spacing(6)};
`
);

function CreateReview() {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t }: { t: any } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { id: reviewId } = useParams();

  const [isSubmit, setIsSubmit] = useState(false);
  const [validationError, setValidationError] = useState('');
  const isEdit = Boolean(reviewId && reviewId.length);

  const { user } = useSelector((state: RootState) => state.app);

  //Queries
  const {
    data: { data: accountRefData } = {},
    isError,
    error,
    isLoading: isRefDataLoading
  } = useQuery('ref-data', getRefData);

  const {
    data: { data: model } = {},
    isLoading,
    refetch: refetchById
  } = useQuery([QKEY.Reviews, reviewId], (k) => getReviewById(reviewId), {
    enabled: isEdit
  });

  const {
    mutate: save,
    isError: isSaveError,
    error: errorOnSave,
    isLoading: isSaving
  } = useMutation((e: ReviewDto) => saveReview(e, reviewId), {
    onSuccess: (o) => onSaveSuccess(o.data)
  });

  //----Queries

  useEffect(() => {}, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  if (isSaveError) {
    const msg = parseApiError(
      errorOnSave,
      `Failed to ${!isEdit ? 'create' : 'update'} review`
    );
    enqueueSnackbar(t(msg), { variant: 'error' });
  }

  const onSaveSuccess = async (updatedModel: Review) => {
    enqueueSnackbar(
      t(`Review was ${!isEdit ? 'created' : 'updated'} successfully`),
      { variant: 'success' }
    );
    if (!isEdit) navigate('/app/review/edit/' + updatedModel.id);
    else {
      // appQueryClient.setQueryData([QKEY.Leads, leadId], updatedModel);
      refetchById();
    }
  };

  const onSaveClick = () => {
    setIsSubmit(true);
  };

  const handleBack = (): void => {
    return navigate(readQueryString()?.ret || `/app/review/list`);
  };

  // if (isLoading || isRefDataLoading) {
  //   return <LinearProgress />;
  // }
  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          firstName: model?.contactFirstName || '',
          middleName: model?.contactMiddleName || '',
          lastName: model?.contactLastName || '',
          contactEmail: model?.contactEmail || '',
          contactNumber: model?.contactNumber || '',
          contactAddress: model?.contactAddress || '',
          remark: model?.remark || '',
          tags: model?.tags || [],
          teamMemberId: model?.teamMemberId || '',
          // profileId: model?.profile?.id || '',
          contactId: model?.contact?.id || '',
          liCyStage: model?.liCyStage || '',
          saveAsContact: true
        }}
        validationSchema={Yup.object().shape({
          firstName: Yup.string().max(250),
          middleName: Yup.string().max(250),
          lastName: Yup.string().max(250),
          contactEmail: Yup.string().when('contactId', {
            is: (o) => !o,
            then: Yup.string()
              .max(250, 'Email must be at most 20 characters')
              .email('Email must be a valid email')
          }),
          contactNumber: Yup.string().when('contactId', {
            is: (o) => !o,
            then: Yup.string().max(
              20,
              'Contact number must be at most 20 digits'
            )
          }),
          contactAddress: Yup.string().max(250),
          remark: Yup.string().max(1000).required('Remark is required')
        })}
        onSubmit={async (
          _values,
          { resetForm, setErrors, setStatus, setSubmitting }
        ) => {
          let valError = '';

          if (!_values.contactId) {
            if (
              !_values.contactEmail &&
              !_values.contactNumber &&
              !_values.firstName &&
              !_values.lastName
            ) {
              valError =
                "Review's contact information is missing. Either select existing contact or enter contact email or contact number or first name or last name";
            }
          }
          setValidationError(valError);
          if (valError) return;

          const updatedModel: ReviewDto = {
            ..._values,
            source: {
              code: user.context.appName,
              value: 'Manual Create'
            }
          };

          save(updatedModel);
          setStatus({ success: true });
          setSubmitting(false);
        }}
      >
        {({ handleSubmit, isSubmitting, values }) => (
          <form onSubmit={handleSubmit}>
            <Box mb={3} display="flex">
              <MainContentWrapper>
                <Grid
                  sx={{
                    px: 4
                  }}
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="stretch"
                  spacing={4}
                >
                  <Grid item xs={12}>
                    <Box
                      mt={3}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <PageHeader
                        isEdit={isEdit}
                        contactId={reviewId}
                        onBack={handleBack}
                      />
                      <Box
                        component="span"
                        sx={{
                          display: { lg: 'none', xs: 'inline-block' }
                        }}
                      >
                        <IconButtonToggle
                          sx={{
                            ml: 2
                          }}
                          color="primary"
                          onClick={handleDrawerToggle}
                          size="small"
                        >
                          <MenuTwoToneIcon />
                        </IconButtonToggle>
                      </Box>
                    </Box>
                  </Grid>
                  {isLoading && (
                    <Grid item xs={12}>
                      <LinearProgress />
                      loading...
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <ReviewInfoCard
                      // profiles={profiles}
                      // members={members}
                      accountRefData={accountRefData}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ReviewContactCard isEdit={isEdit} />
                  </Grid>

                  {/*grs-grid-item <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ display: { xs: 'none', sm: 'block' } }}
                  ></Grid> */}

                  <Grid item xs={12}>
                    <Card
                      sx={{
                        p: 1
                      }}
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Button
                            type="button"
                            component={RouterLink}
                            variant="outlined"
                            to={`/${
                              location.pathname.split('/')[1]
                            }/review/list`}
                          >
                            Cancel
                          </Button>
                          {/* <Button
                            sx={{ ml: 2 }}
                            type="submit"
                            onClick={() => onSaveClick()}
                            variant="contained"
                            startIcon={
                              isSubmitting || isSaving ? (
                                <CircularProgress size="1rem" />
                              ) : (
                                <SaveIcon />
                              )
                            }
                            disabled={isLoading || isSubmitting || isSaving}
                          >
                            Save
                          </Button> */}
                          {validationError && (
                            <FormHelperText error={true}>
                              {validationError}
                            </FormHelperText>
                          )}
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item xs={12}></Grid>
                </Grid>
              </MainContentWrapper>
            </Box>
          </form>
        )}
      </Formik>
    </>
  );
}

export default CreateReview;
